<template>
  <div>
    <!-- MFA Code Entry -->
    <div v-if="step === 'MFA_CODE'">
      <div v-if="firstTime">
        <h1 class="text-center text-base font-medium sm:text-lg pb-4">MFA set-up completed</h1>
        <p class="text-base font-sm sm:text-sm pb-4 text-center">
          To finish signing in, please wait for the next authentication code and enter it into the
          input box below.
        </p>
      </div>
      <div v-else>
        <h1 class="text-center text-base font-medium sm:text-lg pb-4">Keeping you secure</h1>
        <p class="text-base font-sm sm:text-sm pb-4 text-center">
          To finish signing in, please enter your authentication code.
        </p>
      </div>

      <form class="space-y-4" @submit.prevent>
        <div class="flex justify-center">
          <PinInput
            v-model="mfaTokenChars"
            :length="6"
            otp
            :disabled="loading"
            @complete="verifyMFAToken(false)"
            :class="['gap-0', incorrectMFA ? 'ring-2 ring-destructive rounded-lg' : '']"
          >
            <PinInputSlot
              v-for="i in 6"
              :key="i"
              :index="i - 1"
              :class="['h-12 w-12 text-2xl', i === 1 ? 'rounded-l-md' : '', i === 6 ? 'rounded-r-md' : '']"
            />
          </PinInput>
        </div>

        <p class="text-sm text-red-600 text-center" v-if="tokenSame">
          Authentication codes are single use, please enter a different code
        </p>
        <p class="text-sm text-red-600 text-center" v-if="incorrectMFA">
          Authentication code is incorrect, please try again
        </p>

        <Button
          id="signInButton"
          type="submit"
          @click="verifyMFAToken(false)"
          :disabled="mfaToken.length < 6 || tokenSame"
          class="w-full h-10"
        >
          {{ loading ? 'Validating...' : 'Validate code' }}
        </Button>
      </form>
    </div>

    <!-- MFA Setup -->
    <div v-if="step === 'MFA_SETUP'">
      <h1 class="text-center text-lg font-semibold tracking-tight pb-1">
        Scan QR with authenticator app
      </h1>
      <p class="text-center text-sm text-muted-foreground pb-5">
        Scan this QR code with
        <span class="font-medium text-foreground">Google Authenticator</span> or
        <span class="font-medium text-foreground">Microsoft Authenticator</span>.
      </p>

      <form class="space-y-5" @submit.prevent>
        <div class="flex flex-col items-center gap-3">
          <div class="rounded-2xl border border-border bg-white p-4 shadow-sm">
            <qrcode-vue :value="qrCodeValue" :size="200" level="H" render-as="svg" />
          </div>
          <button
            type="button"
            class="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
            @click="showManual = !showManual"
          >
            Can’t scan it?
          </button>
          <p v-if="showManual" class="text-xs text-muted-foreground text-center">
            Enter this key manually:
            <span class="font-mono text-foreground break-all">{{ manualCode }}</span>
          </p>
        </div>

        <div class="space-y-2">
          <p class="text-center text-sm font-medium">Enter the 6-digit code from the app</p>
          <div class="flex justify-center">
            <PinInput
              v-model="mfaTokenChars"
              :length="6"
              otp
              :disabled="loading"
              @complete="verifyMFAToken(true)"
              :class="['gap-0', mfaSetupError ? 'ring-2 ring-destructive rounded-lg' : '']"
            >
              <PinInputSlot
              v-for="i in 6"
              :key="i"
              :index="i - 1"
              :class="['h-12 w-12 text-2xl', i === 1 ? 'rounded-l-md' : '', i === 6 ? 'rounded-r-md' : '']"
            />
            </PinInput>
          </div>
        </div>

        <p class="text-sm text-red-600 text-center" v-if="mfaSetupError">
          Authentication code is incorrect, please try again
        </p>

        <Button
          id="setupMFAButton"
          type="submit"
          @click="verifyMFAToken(true)"
          :disabled="mfaToken.length < 6"
          class="w-full h-10"
        >
          {{ loading ? 'Verifying…' : 'Continue' }}
        </Button>
      </form>
    </div>
  </div>
</template>

<script>
import { Button, PinInput, PinInputSlot } from '@boogi/ui'
import QrcodeVue from 'qrcode.vue'

export default {
  name: 'TwoFactor',
  components: {
    Button,
    PinInput,
    PinInputSlot,
    QrcodeVue,
  },
  data() {
    return {
      step: null,
      session: null,
      email: null,
      qrCodeValue: null,
      firstTime: false,

      mfaTokenChars: [],
      mfaTokenCreation: undefined,
      incorrectMFA: false,
      mfaSetupError: false,
      loading: false,
      showManual: false,
    }
  },
  created() {
    const raw = sessionStorage.getItem('mfaSession')
    if (!raw) {
      this.$router.push('/auth/sign-in')
      return
    }
    const data = JSON.parse(raw)
    this.session = data.session
    this.email = data.email
    this.step = data.type
    if (data.type === 'MFA_SETUP') {
      this.qrCodeValue = data.qrCodeValue
    }
  },
  mounted() {
    document.title = 'Boogi Dashboard | Two-factor authentication'
    this.focusOtpInput()
  },
  computed: {
    mfaToken() {
      return this.mfaTokenChars.join('')
    },
    tokenSame() {
      return this.mfaToken === this.mfaTokenCreation
    },
    manualCode() {
      if (!this.qrCodeValue) return ''
      const match = this.qrCodeValue.match(/[?&]secret=([^&]+)/i)
      return match ? decodeURIComponent(match[1]) : ''
    },
  },
  methods: {
    focusOtpInput() {
      // Only one step (MFA_SETUP / MFA_CODE) is rendered at a time, so the first
      // OTP <input> in the view is the one to focus.
      this.$nextTick(() => {
        this.$el.querySelector('[data-slot="pin-input-slot"]')?.focus()
      })
    },
    async getUser() {
      try {
        const token = localStorage.getItem('token')
        this.loading = true

        const response = await this.$apiFetch(
          `${process.env.VUE_APP_API_URL}/private/api/client/user`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          const object = await response.json()
          const publisher = object.publisher

          localStorage.setItem('firstName', object.publisherUser.firstName)
          localStorage.setItem('lastName', object.publisherUser.lastName)
          localStorage.setItem('publisher', publisher.companyName)
          localStorage.setItem('publisherId', publisher.id)
          localStorage.setItem('publisherStatus', publisher.publisherStatus)
          localStorage.setItem('sandbox', publisher.sandboxOnly)
          localStorage.setItem(
            'sendContentNotifications',
            object.publisherUser.sendContentNotifications
          )

          if (publisher.sandboxOnly) {
            localStorage.setItem('apiUrl', process.env.VUE_APP_SANDBOX_API_URL)
            localStorage.setItem('apiType', process.env.VUE_APP_SANDBOX_API_TYPE)
          } else {
            localStorage.setItem('apiUrl', process.env.VUE_APP_API_URL)
            localStorage.setItem('apiType', process.env.VUE_APP_API_TYPE)
          }

          sessionStorage.removeItem('mfaSession')
          this.$router.push('/')
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.loading = false
      }
    },

    async verifyMFAToken(isFirstTime) {
      this.incorrectMFA = false
      this.mfaSetupError = false

      try {
        const body = {
          mfaToken: this.mfaToken,
          email: this.email,
          session: this.session,
        }

        const response = await this.$apiFetch(
          `${process.env.VUE_APP_API_URL}/private/api/client/auth/verify-mfa?firstTimeSetup=${isFirstTime}`,
          {
            method: 'POST',
            body: JSON.stringify(body),
            headers: { 'Content-Type': 'application/json' },
          }
        )

        if (response.ok) {
          const object = await response.json()

          if (object.status === 'MFA_REQUIRED') {
            // Transition from setup → code entry
            this.step = 'MFA_CODE'
            this.firstTime = true
            this.mfaTokenChars = []
            this.mfaTokenCreation = undefined
          } else {
            const token = 'Bearer ' + object.idToken
            localStorage.setItem('token', token)
            await this.getUser()
          }
        } else {
          if (isFirstTime) {
            this.mfaSetupError = true
          } else {
            this.incorrectMFA = true
          }
        }
      } catch (error) {
        if (isFirstTime) {
          this.mfaSetupError = true
        } else {
          this.incorrectMFA = true
        }
      }
    },
  },
}
</script>

<style></style>
